import React, { useEffect, useState } from "react";
import { getAdminSettings, updateAdminSettings } from "./adminApi";
import "./Settings.css";

const DAYS = [
  { key: "0", label: "Monday" },
  { key: "1", label: "Tuesday" },
  { key: "2", label: "Wednesday" },
  { key: "3", label: "Thursday" },
  { key: "4", label: "Friday" },
  { key: "5", label: "Saturday" },
  { key: "6", label: "Sunday" },
];

const DEFAULT_HOURS = { open: "09:00", close: "22:00", closed: false };

function normalizeHours(source) {
  const hours = source && typeof source === "object" && !Array.isArray(source) ? source : {};
  return DAYS.reduce((result, { key }) => {
    const day = hours[key] && typeof hours[key] === "object" ? hours[key] : {};
    result[key] = {
      open: typeof day.open === "string" ? day.open.slice(0, 5) : DEFAULT_HOURS.open,
      close: typeof day.close === "string" ? day.close.slice(0, 5) : DEFAULT_HOURS.close,
      closed: Boolean(day.closed ?? DEFAULT_HOURS.closed),
    };
    return result;
  }, {});
}

const EMPTY_FOOTER = {
  description: "", facebook_url: "", instagram_url: "", youtube_url: "",
  whatsapp_url: "", privacy_policy_url: "", terms_url: "",
};

const FOOTER_INPUTS = [
  ["facebook_url", "Facebook URL"],
  ["instagram_url", "Instagram URL"],
  ["youtube_url", "YouTube URL"],
  ["whatsapp_url", "WhatsApp URL"],
  ["privacy_policy_url", "Privacy Policy URL"],
  ["terms_url", "Terms & Conditions URL"],
];

function normalizeSettings(data) {
  return { ...data, opening_hours: normalizeHours(data?.opening_hours), footer: { ...EMPTY_FOOTER, ...(data?.footer || {}) } };
}

export default function Settings({ restaurantId }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setForm(null);
    setError("");
    setSuccess("");

    if (!restaurantId) {
      setError("Restaurant identifier is missing.");
      setLoading(false);
      return () => { active = false; };
    }

    getAdminSettings(restaurantId)
      .then((data) => { if (active) setForm(normalizeSettings(data)); })
      .catch((err) => { if (active) setError(err.message || "Unable to load settings."); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [restaurantId]);

  const change = (key, value) => {
    setSuccess("");
    setError("");
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const changeFooter = (key, value) => {
    setSuccess("");
    setError("");
    setForm((previous) => ({
      ...previous,
      footer: { ...EMPTY_FOOTER, ...(previous?.footer || {}), [key]: value },
    }));
  };

  const changeDay = (key, field, value) => {
    setSuccess("");
    setError("");
    setForm((previous) => ({
      ...previous,
      opening_hours: {
        ...previous.opening_hours,
        [key]: { ...DEFAULT_HOURS, ...previous.opening_hours?.[key], [field]: value },
      },
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving || !form) return;
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const changes = {
        opening_hours: normalizeHours(form.opening_hours),
        allow_cash_payment: Boolean(form.allow_cash_payment),
        allow_online_payment: Boolean(form.allow_online_payment),
        tax_percentage: form.tax_percentage,
        service_charge_percentage: form.service_charge_percentage,
        ...EMPTY_FOOTER, ...(form.footer || {}),
      };
      const result = await updateAdminSettings(restaurantId, changes);
      setForm(normalizeSettings({ ...form, ...result }));
      setSuccess("Settings saved successfully.");
    } catch (err) {
      setError(err.message || "Unable to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="rs-status">Loading settings...</div>;
  if (!form) return <div className="rs-status rs-error" role="alert">{error || "Settings unavailable."}</div>;

  return (
    <form className="rs-page" onSubmit={save}>
      <div className="rs-heading">
        <div>
          <h2>Restaurant Settings</h2>
          <p>Manage your restaurant preferences and weekly opening hours.</p>
        </div>
        <button type="submit" className="rs-save" disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {error && <div className="rs-status rs-error" role="alert">{error}</div>}
      {success && <div className="rs-status rs-success" role="status">{success}</div>}

      <section className="rs-panel">
        <div className="rs-panel-heading">
          <h3>Operating Hours</h3>
          <p>Set the opening and closing times for each day of the week.</p>
        </div>
        <div className="rs-hours">
          {DAYS.map(({ key, label }) => {
            const hours = { ...DEFAULT_HOURS, ...form.opening_hours?.[key] };
            return (
              <div className={`rs-day${hours.closed ? " rs-day-closed" : ""}`} key={key}>
                <strong className="rs-day-name">{label}</strong>
                <label className="rs-toggle">
                  <input type="checkbox" checked={hours.closed}
                    onChange={(event) => changeDay(key, "closed", event.target.checked)} />
                  <span>Closed</span>
                </label>
                <label className="rs-time-field">
                  <span>Opens</span>
                  <input type="time" disabled={hours.closed} value={hours.open}
                    onChange={(event) => changeDay(key, "open", event.target.value)} />
                </label>
                <label className="rs-time-field">
                  <span>Closes</span>
                  <input type="time" disabled={hours.closed} value={hours.close}
                    onChange={(event) => changeDay(key, "close", event.target.value)} />
                </label>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rs-panel">
        <div className="rs-panel-heading">
          <h3>Payment Methods</h3>
          <p>Choose which payment methods customers can use.</p>
        </div>
        <div className="rs-settings-list">
          <label className="rs-setting">
            <span>Allow Cash Payment</span>
            <input type="checkbox" checked={Boolean(form.allow_cash_payment)}
              onChange={(event) => change("allow_cash_payment", event.target.checked)} />
          </label>
          <label className="rs-setting">
            <span>Allow Online Payment</span>
            <input type="checkbox" checked={Boolean(form.allow_online_payment)}
              onChange={(event) => change("allow_online_payment", event.target.checked)} />
          </label>
        </div>
      </section>

      <section className="rs-panel">
        <div className="rs-panel-heading">
          <h3>Taxes &amp; Service Charges</h3>
          <p>Percentages applied to customer orders.</p>
        </div>
        <div className="rs-numbers">
          <label>
            <span>Tax (%)</span>
            <input type="number" min="0" max="100" step="0.01" required
              value={form.tax_percentage ?? "0"}
              onChange={(event) => change("tax_percentage", event.target.value)} />
          </label>
          <label>
            <span>Service Charge (%)</span>
            <input type="number" min="0" max="100" step="0.01" required
              value={form.service_charge_percentage ?? "0"}
              onChange={(event) => change("service_charge_percentage", event.target.value)} />
          </label>
        </div>
        <small className="rs-currency">Currency: {form.currency || "INR"}</small>
      </section>

      <section className="rs-panel">
        <div className="rs-panel-heading">
          <h3>Footer &amp; Social Media</h3>
          <p>Add your restaurant's public social profiles and footer information. Leave a link blank to hide it where supported by the theme.</p>
        </div>
        <div className="rs-footer-fields">
          <label>
            <span>Footer Description</span>
            <textarea rows={3} maxLength={10000} value={form.footer?.description ?? ""}
              onChange={(event) => changeFooter("description", event.target.value)}
              placeholder="Tell customers about your restaurant" />
          </label>
          {FOOTER_INPUTS.map(([key, label]) => (
            <label key={key}>
              <span>{label}</span>
              <input type="url" maxLength={200} value={form.footer?.[key] ?? ""}
                onChange={(event) => changeFooter(key, event.target.value)}
                placeholder="https://" />
            </label>
          ))}
        </div>
      </section>

      <div className="rs-bottom-actions">
        <button type="submit" className="rs-save rs-bottom" disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
}